import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  TextField,
  Autocomplete,
  Grid,
  Typography,
  Button,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Cookie from "js-cookie";
import { StyledCancelButton, StyledButton } from "components/StyledComponents/styledComponents";
import URLs from "constants/urls";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default function ConnectorPaymentsDialog({
  open,
  onClose,
  startDate,
  endDate,
  dsaId,
  user,
}) {
  const gridRef = useRef(null); // AG Grid ref for accurate filtered export

  const [rowData, setRowData] = useState([]);
  const [filteredRowData, setFilteredRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const [searchColumn, setSearchColumn] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const jwt_token = Cookie.get("jwt_token");

  const formatIndianCurrency = (value) => {
    if (value == null) return "";
    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Define all columns
  const allColumnDefs = useMemo(
    () => [
      {
        headerName: "Remarks",
        field: "remarks",
        sortable: true,
        filter: true,
        flex: 2,
      },
      {
        headerName: "Amount",
        field: "amount",
        sortable: true,
        filter: true,
        valueFormatter: (params) => formatIndianCurrency(params.value),
        cellStyle: (params) => ({
          textAlign: "right",
          fontWeight:
            params.data?.remarks === "Total" || params.data?.remarks === "Total (Filtered)"
              ? "bold"
              : "500",
        }),
        flex: 1,
        type: "numericColumn",
      },
      {
        headerName: "TransactionDate",
        field: "tranDate",
        sortable: true,
        filter: true,
      },
      {
        headerName: "ConnectorAccName",
        field: "connectorActNames",
        sortable: true,
        filter: true,
      },
      {
        headerName: "KHAccountName",
        field: "khsacctNames",
        sortable: true,
        filter: true,
      },
    ],
    []
  );

  // Initialize column options for search
  useEffect(() => {
    const options = allColumnDefs
      .filter((col) => col.field)
      .map((col) => ({
        label: col.headerName,
        value: col.field,
        field: col.field,
        type: col.type || "string",
      }));
    setColumnOptions(options);

    if (options.length > 0 && !searchColumn) {
      setSearchColumn(options[0]);
    }
  }, [allColumnDefs, searchColumn]);

  // Filter data based on custom search
  useEffect(() => {
    if (!searchText.trim() || !searchColumn) {
      setFilteredRowData(rowData);
      return;
    }

    const lowerSearchText = searchText.toLowerCase();
    const filtered = rowData.filter((row) => {
      const fieldValue = row[searchColumn.field];
      if (fieldValue == null) return false;

      if (searchColumn.type === "numericColumn" || typeof fieldValue === "number") {
        return fieldValue.toString().includes(searchText);
      }

      return fieldValue.toString().toLowerCase().includes(lowerSearchText);
    });

    setFilteredRowData(filtered);
  }, [rowData, searchColumn, searchText]);

  // Calculate footer totals
  const calculateFooterTotals = useCallback((data) => {
    if (!data || data.length === 0) {
      return { amount: 0 };
    }
    return {
      amount: data.reduce((sum, row) => sum + (row.amount || 0), 0),
    };
  }, []);

  // Update pinned bottom row
  useEffect(() => {
    const dataToUse = searchText && searchColumn ? filteredRowData : rowData;
    const totals = calculateFooterTotals(dataToUse);

    const footerRow = {
      remarks: searchText && searchColumn ? "Total (Filtered)" : "Total",
      amount: totals.amount,
      tranDate: "",
      connectorActNames: "",
      khsacctNames: "",
    };

    setPinnedBottomRowData([footerRow]);
  }, [rowData, filteredRowData, searchText, searchColumn, calculateFooterTotals]);

  const handleSearchColumnChange = (event, newValue) => {
    setSearchColumn(newValue);
    if (!newValue) {
      setSearchText("");
    }
  };

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchText("");
  };

  // ==================== IMPROVED DOWNLOAD EXCEL (Using Grid API) ====================
  const handleDownloadExcel = () => {
    let dataToExport = [];

    // Prefer AG Grid API to get rows after filter (most accurate)
    if (gridRef.current?.api) {
      gridRef.current.api.forEachNodeAfterFilter((node) => {
        if (node.data) {
          dataToExport.push(node.data);
        }
      });
    } else {
      // Fallback
      dataToExport = searchText && searchColumn ? filteredRowData : rowData;
    }

    if (!dataToExport || dataToExport.length === 0) {
      alert("No data available to export");
      return;
    }

    // Recalculate totals based on rows being exported
    const filteredTotals = calculateFooterTotals(dataToExport);

    const filteredFooterRow = {
      remarks: searchText && searchColumn ? "Total (Filtered)" : "Total",
      amount: filteredTotals.amount,
      tranDate: "",
      connectorActNames: "",
      khsacctNames: "",
    };

    const allData = [...dataToExport, filteredFooterRow];

    // Prepare data for Excel
    const exportData = allData.map((row) => ({
      Remarks: row.remarks || "",
      Amount: row.amount || 0,
      TranDate: row.tranDate || "",
      ConnectorActNames: row.connectorActNames || "",
      KhsacctNames: row.khsacctNames || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Connector Payments");

    // Auto-size columns
    const maxWidths = [];
    exportData.forEach((row) => {
      Object.values(row).forEach((val, i) => {
        const len = val ? val.toString().length : 10;
        maxWidths[i] = Math.max(maxWidths[i] || 10, len);
      });
    });
    worksheet["!cols"] = maxWidths.map((w) => ({ wch: w + 3 }));

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `Connector_Payments_${startDate}_to_${endDate}.xlsx`
    );
  };
  // =====================================================================

  // Fetch data
  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const url = `${URLs.connectorBrekdownUrl}?startDate=${startDate}&endDate=${endDate}&dsaId=${dsaId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        });

        if (!response.ok) throw new Error("Network response was not ok");

        const data = await response.json();

        setRowData(data || []);
        setFilteredRowData(data || []);
      } catch (error) {
        console.error("Error fetching connector payments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, startDate, endDate, dsaId, jwt_token]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Connector Payments</DialogTitle>

      {/* Search Controls */}
      <DialogContent style={{ padding: "0 24px" }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2, mt: 0.1 }}>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={columnOptions}
              getOptionLabel={(option) => option.label}
              value={searchColumn}
              onChange={handleSearchColumnChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Column"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
              disableClearable={false}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search Text"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={handleSearchTextChange}
              placeholder={
                searchColumn ? `Search in ${searchColumn.label}...` : "Select a column first"
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              disabled={!searchText}
              sx={{ mr: 1, color: "#221f1f" }}
            >
              Clear Search
            </Button>
            <Typography variant="caption" color="#221f1f">
              {searchText && searchColumn
                ? `Showing ${filteredRowData.length} of ${rowData.length} rows`
                : `Total ${rowData.length} rows`}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Grid Content */}
      <DialogContent style={{ height: 400, position: "relative", paddingTop: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <div className="ag-theme-alpine" style={{ height: "100%", width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={searchText && searchColumn ? filteredRowData : rowData}
              columnDefs={allColumnDefs}
              pinnedBottomRowData={pinnedBottomRowData}
              defaultColDef={{
                resizable: true,
                flex: 1,
                minWidth: 120,
              }}
              getRowStyle={(params) => {
                if (params.node.rowPinned === "bottom") {
                  return {
                    fontWeight: "bold",
                    backgroundColor: "#f5f6f8",
                  };
                }
                return null;
              }}
              pagination
              paginationPageSize={100}
            />
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <StyledButton
          variant="contained"
          color="primary"
          onClick={handleDownloadExcel}
          sx={{ textTransform: "none" }}
          disabled={rowData.length === 0}
        >
          Download Excel
        </StyledButton>
        <StyledCancelButton onClick={onClose} variant="outlined">
          Close
        </StyledCancelButton>
      </DialogActions>
    </Dialog>
  );
}

ConnectorPaymentsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  dsaId: PropTypes.number.isRequired,
  user: PropTypes.object.isRequired,
};
